/**
 * ScreenplayTile.js
 * ----------------------------------------------------------------------
 * Renders the “Detailed Screenplay” tile.
 * - JSON is the source of truth for screenplay content.
 * - Edit mode shows a full, editable plaintext rendering generated from JSON.
 * - Cards view shows normalized per-scene info with rich sections.
 * - Shot Matrix is rendered from strict JSON with consistent indentation, bold “Beat N:”,
 *   and “Shot A/B/…” headers with Prompt/Audio/Micro-motion bullets.
 *
 * Props expected (backward-tolerant):
 *   detailedScreenplay        (object | string)  // JSON object preferred
 *   isLoading                 (bool)
 *   isEditing                 (bool)
 *   onSaveEditedScreenplay    (fn)
 *   onCancelEdit              (fn)
 *   onGenerateScenes          (fn)
 *   isGeneratingScenes        (bool)
 *   maximizeScreenPlay        (bool)
 *   toggleMaximizeScreenPlay  (fn)
 *   screenplayOutline         (object|null)      // { title, scenes:[{ number,title,synopsis,characters[] }] }
 *   isDetailsGenerating       (bool)
 *   hydratedScenesByNumber    (object|array)
 *
 * NOTE: This file is self-contained and does not depend on legacy text parsers.
 */
import React, { useMemo, useEffect, useState } from 'react';
import { renderDetailedScreenplayText } from '../utilities/utility';
import './ScreenplayTile.css';

/* =========================================================================
 * Utilities (scoped)
 * ========================================================================= */

function sceneId(s, idx) {
  const n = s?.number ?? (idx + 1);
  return String(n);
}

function IND(n) { return '  '.repeat(n); }
function esc(s) {
  return String(s ?? '')
    .replace(/&/g, '&amp;').replace(/</g, '&lt;')
    .replace(/>/g, '&gt;').replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}
function joinLines(x) { return Array.isArray(x) ? x.filter(Boolean).join('\n') : (x || ''); }
function toArray(x) {
  return Array.isArray(x)
    ? x.filter(v => v != null && v !== '')
    : (x ? String(x).split('\n').map(s => s.trim()).filter(Boolean) : []);
}
function toKeyValList(obj) {
  if (!obj || typeof obj !== 'object') return [];
  return Object.entries(obj)
    .map(([k, v]) => `${String(k || '').trim()}: ${String(v || '').trim()}`)
    .filter(Boolean);
}
function normalizeDialogues(x) {
  const arr = Array.isArray(x) ? x : toArray(x);
  return arr.map(d => {
    if (typeof d === 'string') return d.trim();
    if (d && typeof d === 'object') {
      const text = [d.character, d.line].filter(Boolean).join(': ');
      return text.trim();
    }
    return String(d || '').trim();
  }).filter(Boolean);
}
function normalizeCoverage(val) {
  if (!val) return null;
  if (typeof val === 'string') return val.trim() ? { coverage: val.trim() } : null;
  if (typeof val === 'object') {
    const coverage = joinLines(val.coverage);
    const transitions = joinLines(val.transitions);
    const editorialIntent = joinLines(val.editorial_intent || val.editorialIntent);
    if (coverage || transitions || editorialIntent) {
      return { coverage, transitions, editorialIntent };
    }
  }
  return null;
}
function normalizeLighting(val, fallback) {
  if (typeof val === 'string') return val;
  if (Array.isArray(val)) return joinLines(val);
  if (val && typeof val === 'object') return joinLines(val.text || val.notes || '');
  return fallback || '';
}

function normalizeSceneBible(sb) {
  if (!sb || typeof sb !== 'object') return null;
  return {
    lens_mm: sb.lens_mm,
    aspect: sb.aspect,
    palette: sb.palette,
    grade: sb.grade,
    time_of_day: sb.time_of_day,
    continuity_notes: Array.isArray(sb.continuity_notes) ? sb.continuity_notes : [],
  };
}





/* =========================================================================
 * JSON → Tile scene objects
 * ========================================================================= */
function buildScenesForTile(detailedScreenplay) {
  try {
    if (!detailedScreenplay) return [];
    if (typeof detailedScreenplay !== 'object') return []; // JSON-only

    const js = detailedScreenplay;
    const scenes = Array.isArray(js?.scenes) ? js.scenes : [];
    return scenes.map((h, idx) => {
      const num = h?.number ?? (idx + 1);
      const duration =
        h?.duration_minutes != null && !Number.isNaN(Number(h.duration_minutes))
          ? `${Math.max(1, Math.round(Number(h.duration_minutes)))} min`
          : '';

      return {
        number: num,
        title: (h?.title && String(h.title).trim()) || `Scene ${num}`,
        duration,
        synopsis: h?.summary || h?.synopsis || h?.logline || '',
        visualPrompt: joinLines(h?.visual_prompt) || h?.visual || '',
        sceneImagePrompt: h?.scene_image_prompt || '',
        environment: h?.environment || '',
        coveragePlan: normalizeCoverage(h?.coverage_plan),
        lightingPlan: normalizeLighting(h?.lighting_plan, joinLines(h?.lighting)),
        backgroundMusic: h?.background_music || (h?.music?.cue || ''),
        characterExperessions: toKeyValList(h?.character_expressions),
        characterAttire: toKeyValList(h?.character_attire),
        dialogues: normalizeDialogues(h?.dialogues || h?.dialogue),
        characters: Array.isArray(h?.characters) ? h.characters : [],
        sceneBible: normalizeSceneBible(h?.scene_bible),
        shotMatrix: Array.isArray(h?.shot_matrix) ? h.shot_matrix : [],
        fx: h?.fx || '',
      };
    });
  } catch (e) {
    console.warn('[ScreenplayTile] buildScenesForTile failed; returning [].', e);
    return [];
  }
}

function makeDraftFromScene(sceneJson) {
  // Build a user-friendly, flat draft. Keep types friendly for inputs.
  const coverage = normalizeCoverage(sceneJson?.coverage_plan) || {};
  const sb = sceneJson?.scene_bible || {};
  const dialogues = Array.isArray(sceneJson?.dialogues || sceneJson?.dialogue)
    ? (sceneJson.dialogues || sceneJson.dialogue).map(d => {
      if (typeof d === 'string') return d;
      if (d && typeof d === 'object') {
        const t = [d.character, d.line].filter(Boolean).join(': ');
        return t;
      }
      return '';
    })
    : [];

  return {
    // basics
    title: String(sceneJson?.title || ''),
    duration_minutes: String(sceneJson?.duration_minutes ?? ''),
    synopsis: String(sceneJson?.summary || sceneJson?.synopsis || sceneJson?.logline || ''),
    visual: String(joinLines(sceneJson?.visual_prompt) || sceneJson?.visual || ''),
    scene_image_prompt: String(sceneJson?.scene_image_prompt || ''),
    environment: String(sceneJson?.environment || ''),
    background_music: String(sceneJson?.background_music || (sceneJson?.music?.cue || '')),

    // structured
    coverage: String(coverage.coverage || ''),
    transitions: String(coverage.transitions || ''),
    editorial_intent: String(coverage.editorialIntent || coverage.editorial_intent || ''),
    lighting_plan: String(normalizeLighting(sceneJson?.lighting_plan, joinLines(sceneJson?.lighting))),

    // key/value maps
    character_expressions: sceneJson?.character_expressions && typeof sceneJson.character_expressions === 'object'
      ? Object.entries(sceneJson.character_expressions).map(([k, v]) => ({ key: k, value: String(v ?? '') }))
      : [],
    character_attire: sceneJson?.character_attire && typeof sceneJson.character_attire === 'object'
      ? Object.entries(sceneJson.character_attire).map(([k, v]) => ({ key: k, value: String(v ?? '') }))
      : [],

    shot_matrix_json: JSON.stringify(sceneJson?.shot_matrix ?? [], null, 2),


    // multi-line list
    dialogues,

    // scene bible (object)
    scene_bible: {
      lens_mm: sb.lens_mm ?? '',
      aspect: sb.aspect ?? '',
      palette: sb.palette ?? '',
      grade: sb.grade ?? '',
      time_of_day: sb.time_of_day ?? '',
      continuity_notes: Array.isArray(sb.continuity_notes) ? sb.continuity_notes.join('\n') : ''
    }

  };
}

function applyDraftToScene(origSceneJson, draft) {
  const next = { ...(origSceneJson || {}) };
  // basics
  next.title = draft.title?.trim() || next.title || '';
  if (draft.duration_minutes !== '') {
    const n = Number(draft.duration_minutes);
    if (!Number.isNaN(n)) next.duration_minutes = n;
  }
  if (draft.synopsis?.trim()) {
    // prefer writing into "summary" to avoid clobbering older fields
    next.summary = draft.synopsis.trim();
  } else {
    delete next.summary;
  }
  if (draft.visual?.trim()) {
    next.visual = draft.visual.trim(); // keep as string for readability
    delete next.visual_prompt;
  }
  next.scene_image_prompt = draft.scene_image_prompt?.trim() || '';
  next.environment = draft.environment?.trim() || '';
  next.background_music = draft.background_music?.trim() || '';

  // structured coverage_plan
  const coverage_plan = {
    coverage: draft.coverage?.trim() || '',
    transitions: draft.transitions?.trim() || '',
    editorial_intent: draft.editorial_intent?.trim() || ''
  };
  // keep only if any field present
  if (coverage_plan.coverage || coverage_plan.transitions || coverage_plan.editorial_intent) {
    next.coverage_plan = coverage_plan;
  } else {
    delete next.coverage_plan;
  }

  // lighting
  next.lighting_plan = draft.lighting_plan?.trim() || '';

  // key/value maps
  const kvToObj = (arr) =>
    (Array.isArray(arr) ? arr : [])
      .filter(x => x && x.key && String(x.key).trim())
      .reduce((acc, { key, value }) => { acc[String(key).trim()] = String(value ?? '').trim(); return acc; }, {});
  const exprObj = kvToObj(draft.character_expressions);
  const attireObj = kvToObj(draft.character_attire);
  if (Object.keys(exprObj).length) next.character_expressions = exprObj; else delete next.character_expressions;
  if (Object.keys(attireObj).length) next.character_attire = attireObj; else delete next.character_attire;

  // dialogues
  const diags = Array.isArray(draft.dialogues) ? draft.dialogues.map(l => String(l || '').trim()).filter(Boolean) : [];
  next.dialogues = diags.map(line => {
    const i = line.indexOf(':');
    if (i > -1) {
      return { character: line.slice(0, i).trim(), line: line.slice(i + 1).trim() };
    }
    return line; // leave as plain string if no "Name: text"
  });

  if (typeof draft.shot_matrix_json === 'string') {
    try {
      const parsed = JSON.parse(draft.shot_matrix_json);
      if (Array.isArray(parsed)) {
        next.shot_matrix = parsed;
      }
    } catch {
      // ignore parse errors; keep existing shot_matrix unchanged
    }
  }

  // scene_bible
  const sb = {
    lens_mm: draft.scene_bible?.lens_mm || '',
    aspect: draft.scene_bible?.aspect || '',
    palette: draft.scene_bible?.palette || '',
    grade: draft.scene_bible?.grade || '',
    time_of_day: draft.scene_bible?.time_of_day || '',
    continuity_notes: draft.scene_bible?.continuity_notes
      ? String(draft.scene_bible.continuity_notes).split('\n').map(s => s.trim()).filter(Boolean)
      : []
  };
  // keep only when at least one field is populated
  if (Object.values(sb).some(v => (Array.isArray(v) ? v.length : String(v || '').trim()))) {
    next.scene_bible = sb;
  } else {
    delete next.scene_bible;
  }
  return next;
}


function KVListEditor({ label, items, onChange }) {
  const update = (idx, patch) => {
    const next = items.slice();
    next[idx] = { ...next[idx], ...patch };
    onChange(next);
  };
  const add = () => onChange([...(items || []), { key: '', value: '' }]);
  const del = (idx) => onChange(items.filter((_, i) => i !== idx));
  return (
    <div className="kv-editor">
      <div className="field-label">{label}</div>
      {(items || []).map((row, i) => (
        <div key={i} className="kv-row" style={{ display: 'grid', gridTemplateColumns: '1fr 2fr auto', gap: 8, marginBottom: 6 }}>
          <input value={row.key || ''} placeholder="key (e.g. Emma)" onChange={e => update(i, { key: e.target.value })} />
          <input value={row.value || ''} placeholder="value" onChange={e => update(i, { value: e.target.value })} />
          <button onClick={() => del(i)} title="Remove">×</button>
        </div>
      ))}
      <button onClick={add}>+ Add</button>
    </div>
  );
}

function LinesEditor({ label, lines, onChange, placeholder }) {
  const value = (lines || []).join('\n');
  return (
    <div className="lines-editor">
      <div className="field-label">{label}</div>
      <textarea rows={5} value={value} placeholder={placeholder || ''}
        onChange={e => onChange(e.target.value.split('\n'))} />
    </div>
  );
}


/* =========================================================================
 * Shot Matrix renderer (JSON only) → HTML inside <pre>
 * ========================================================================= */
/* =========================================================================
 * Shot Matrix Table Renderer (Elegant)
 * ========================================================================= */
const ShotMatrixTable = ({ rows }) => {
  if (!Array.isArray(rows) || rows.length === 0) return null;

  return (
    <table className="shot-matrix-table" style={{ width: '100%', borderCollapse: 'collapse', marginTop: 8 }}>
      <thead>
        <tr style={{ borderBottom: '1px solid #333', textAlign: 'left' }}>
          <th style={{ padding: 8, color: '#888', fontSize: '0.75em', textTransform: 'uppercase' }}>Beat</th>
          <th style={{ padding: 8, color: '#888', fontSize: '0.75em', textTransform: 'uppercase' }}>ID</th>
          <th style={{ padding: 8, color: '#888', fontSize: '0.75em', textTransform: 'uppercase' }}>Dur</th>
          <th style={{ padding: 8, color: '#888', fontSize: '0.75em', textTransform: 'uppercase' }}>Shot Type</th>
          <th style={{ padding: 8, color: '#888', fontSize: '0.75em', textTransform: 'uppercase' }}>Camera</th>
          <th style={{ padding: 8, color: '#888', fontSize: '0.75em', textTransform: 'uppercase' }}>Lighting</th>
          <th style={{ padding: 8, color: '#888', fontSize: '0.75em', textTransform: 'uppercase' }}>Movement</th>
          <th style={{ padding: 8, color: '#888', fontSize: '0.75em', textTransform: 'uppercase' }}>Visual</th>
          <th style={{ padding: 8, color: '#888', fontSize: '0.75em', textTransform: 'uppercase' }}>Dialogue</th>
          <th style={{ padding: 8, color: '#888', fontSize: '0.75em', textTransform: 'uppercase' }}>Audio</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((row, i) =>
          (Array.isArray(row?.shots) ? row.shots : []).map((sh, j) => (
            <tr key={`${i}-${j}`} style={{ borderBottom: '1px solid #1a1a1a' }}>
              <td className="sm-beat" style={{ padding: 8, verticalAlign: 'top', color: '#666', fontSize: '0.85em' }}>{row?.beat}</td>
              <td className="sm-id-cell" style={{ padding: 8, verticalAlign: 'top' }}>
                <span className="sm-id-badge" style={{ background: '#222', color: '#ddd', padding: '2px 6px', borderRadius: 4, fontSize: '0.85em', border: '1px solid #333' }}>{sh?.id}</span>
              </td>
              <td className="sm-beat" style={{ padding: 8, verticalAlign: 'top', color: '#888', fontSize: '0.85em' }}>
                {sh?.duration_sec ? `${sh.duration_sec}s` : '--'}
              </td>
              <td className="sm-specs" style={{ padding: 8, verticalAlign: 'top', color: '#ccc', fontSize: '0.9em' }}>
                {sh?.size || sh?.angle ? (
                  <span>
                    {sh?.size && <span style={{ color: '#fff' }}>{sh.size}</span>}
                    {sh?.size && sh?.angle && ' '}
                    {sh?.angle && <span style={{ color: '#bbb' }}>{sh.angle}</span>}
                  </span>
                ) : '--'}
              </td>
              <td className="sm-dim" style={{ padding: 8, verticalAlign: 'top', color: '#777', fontSize: '0.85em' }}>
                {sh?.lens_characteristics && <div>{sh.lens_characteristics}</div>}
                {sh?.depth_of_field && <div>{sh.depth_of_field}</div>}
                {!sh?.lens_characteristics && !sh?.depth_of_field && '--'}
              </td>
              <td className="sm-dim" style={{ padding: 8, verticalAlign: 'top', color: '#777', fontSize: '0.85em' }}>
                {sh?.exposure_style && <div>{sh.exposure_style}</div>}
                {sh?.post_grade_intent && <div>{sh.post_grade_intent}</div>}
                {!sh?.exposure_style && !sh?.post_grade_intent && '--'}
              </td>
              <td className="sm-dim" style={{ padding: 8, verticalAlign: 'top', color: '#777', fontSize: '0.85em' }}>
                {sh?.camera_motion_vector || sh?.transition || '--'}
              </td>
              <td className="sm-details-full" style={{ padding: 8, verticalAlign: 'top', color: '#ccc', fontSize: '0.9em', lineHeight: 1.5 }}>
                {sh?.prompt || '--'}
              </td>
              <td className="sm-details-full" style={{ padding: 8, verticalAlign: 'top', color: '#ccc', fontSize: '0.9em', lineHeight: 1.5, fontStyle: 'italic', maxWidth: '200px' }}>
                {sh?.dialogue ? `"${sh.dialogue}"` : '--'}
              </td>
              <td className="sm-dim" style={{ padding: 8, verticalAlign: 'top', color: '#777', fontSize: '0.85em' }}>
                {Array.isArray(sh?.audio) ? sh.audio.join(', ') : (sh?.audio || '--')}
              </td>
            </tr>
          ))
        )}
      </tbody>
    </table>
  );
};

/* =========================================================================
 * Component
 * ========================================================================= */
function ScreenplayTile(props) {
  const {
    detailedScreenplay,
    isLoading = false,
    onGenerateScenes,
    isGeneratingScenes = false,
    isEditing = false,
    onSaveEditedScreenplay,
    maximizeScreenPlay = true,
    toggleMaximizeScreenPlay,
    onCancelEdit,
    screenplayOutline = null,
    isDetailsGenerating = false,
    hydratedScenesByNumber = {},
    isExpanded = false,
    onValidateContent,
  } = props;


  // Seed editor with FULL text
  const [editedScreenplay, setEditedScreenplay] = useState(
    typeof detailedScreenplay === 'object'
      ? renderDetailedScreenplayText(detailedScreenplay)
      : (detailedScreenplay || '')
  );
  useEffect(() => {
    setEditedScreenplay(
      typeof detailedScreenplay === 'object'
        ? renderDetailedScreenplayText(detailedScreenplay)
        : (detailedScreenplay || '')
    );
  }, [detailedScreenplay]);

  // Build scenes (JSON only)
  const parsedDetailedScenes = useMemo(
    () => buildScenesForTile(detailedScreenplay),
    [detailedScreenplay]
  );

  // Outline fallback (used only if no detailed scenes available)
  const outlineScenes = useMemo(() => {
    const ol = screenplayOutline && Array.isArray(screenplayOutline.scenes)
      ? screenplayOutline.scenes
      : [];
    return ol.map(sc => ({
      number: sc.number,
      title: sc.title,
      synopsis: sc.synopsis,
      characters: Array.isArray(sc.characters) ? sc.characters : [],
    }));
  }, [screenplayOutline]);

  // Expanded/collapsed per-scene state
  const [open, setOpen] = useState(new Set());
  const toggleOpen = (num) => {
    setOpen(prev => {
      const next = new Set(prev);
      if (next.has(num)) next.delete(num); else next.add(num);
      return next;
    });
  };

  const handleShowHideScreenplay = () => {
    toggleMaximizeScreenPlay && toggleMaximizeScreenPlay();
  };

  /* ============================
   * Editor (full text)
   * ============================ */
  const renderEditor = () => (
    <div>
      <textarea
        className="screenplay-edit-area"
        value={editedScreenplay || ''}
        onChange={(e) => setEditedScreenplay(e.target.value)}
        rows={100}
        style={{ width: '100%', fontSize: 16, resize: 'vertical' }}
      />
      <div className="buttons-container" style={{ marginTop: 10 }}>
        <button
          className="sp-button primary"
          onClick={() => {
            onSaveEditedScreenplay && onSaveEditedScreenplay(editedScreenplay);
          }}
          disabled={isLoading}
        >
          Save
        </button>
        <button
          className="sp-button"
          disabled={isLoading}
          onClick={() => {
            setEditedScreenplay(
              typeof detailedScreenplay === 'object'
                ? renderDetailedScreenplayText(detailedScreenplay)
                : (detailedScreenplay || '')
            );
            onCancelEdit && onCancelEdit();
          }}
        >
          Cancel
        </button>
      </div>
    </div>
  );

  /* ============================
   * Raw preview (top-level full text)
   * ============================ */
  const [showFullScreenplay, setShowFullScreenplay] = useState(false);
  const [editingSceneId, setEditingSceneId] = useState(null);
  const [validatingSceneId, setValidatingSceneId] = useState(null);
  const [validationErrors, setValidationErrors] = useState({});
  const [draftsById, setDraftsById] = useState({});

  const renderRawPreview = () => {
    const raw = (typeof detailedScreenplay === 'object'
      ? renderDetailedScreenplayText(detailedScreenplay)
      : (detailedScreenplay || '')
    );
    if (!raw) return null;
    const lines = raw.split('\n');
    const previewLines = lines.slice(0, 10).join('\n');

    return (
      <div>
        <pre>{showFullScreenplay ? raw : previewLines}</pre>
        <div className="buttons-container">
          <button
            className="show-more-button"
            onClick={() => setShowFullScreenplay(!showFullScreenplay)}
          >
            {showFullScreenplay ? 'Show Less' : 'Show More'}
          </button>
        </div>
      </div>
    );
  };

  /* ============================
   * Elegant Components (matched to NarrativeWorkbench)
   * ============================ */
  const Section = ({ title, children, className = '' }) => (
    <div className={`nw-info-block ${className}`}>
      <div className="nw-label">{title}</div>
      <div className="nw-value">{children}</div>
    </div>
  );

  const CoverageBlock = ({ plan }) => {
    if (!plan) return null;
    let pairs = [];
    if (typeof plan === 'string') {
      pairs = [['Coverage', plan]];
    } else if (typeof plan === 'object') {
      const { coverage, transitions, editorialIntent } = plan;
      if (coverage) pairs.push(['Coverage', coverage]);
      if (transitions) pairs.push(['Transitions', transitions]);
      if (editorialIntent) pairs.push(['Editorial Intent', editorialIntent]);
    }
    if (!pairs.length) return null;
    return (
      <div className="nw-info-block">
        <div className="nw-label">Coverage Plan</div>
        <ul className="nw-list">
          {pairs.map(([k, v], i) => (
            <li key={i}><span className="nw-list-label">{k}:</span> {v}</li>
          ))}
        </ul>
      </div>
    );
  };

  /* ============================
   * Cards / Outline / Details
   * ============================ */
  const hasOutline = Array.isArray(outlineScenes) && outlineScenes.length > 0;
  const detailsExist = Array.isArray(parsedDetailedScenes) && parsedDetailedScenes.length > 0;

  const body = (() => {
    if (isLoading && !hasOutline && !detailsExist) {
      return (
        <img
          src={`${process.env.PUBLIC_URL}/static/media/loading1.eb6327c2b020fd151d29.gif`}
          alt="Loading..."
          className="loading-animation"
        />
      );
    }

    //if (isEditing) return renderEditor();

    if (!detailsExist && hasOutline) {
      return (
        <div className="sp-cards">
          {(isExpanded ? outlineScenes : outlineScenes.slice(0, 5)).map((s, idx) => {
            const id = sceneId(s, idx);
            const isOpen = open.has(id);
            return (
              <div key={id} className={`sp-card ${isOpen ? 'open' : ''}`} onClick={() => toggleOpen(id)} role="button" tabIndex={0}>
                <div className="sp-header">
                  <div className="sp-title">
                    <span className="sp-heading">
                      <strong>{s.number}. {s.title || `Scene ${s.number}`}</strong>
                    </span>
                  </div>
                  {isDetailsGenerating && (
                    <img
                      src={`${process.env.PUBLIC_URL}/static/media/loading1.eb6327c2b020fd151d29.gif`}
                      alt="Loading..."
                      className="loading-badge"
                      title="Hydrating details…"
                    />
                  )}
                </div>
                <div className="sp-subtitle">
                  {s.synopsis || ''}
                </div>
                {Array.isArray(s.characters) && s.characters.length ? (
                  <div className="sp-characters">
                    {s.characters.join(' · ')}
                  </div>
                ) : null}

              </div>
            );
          })}
        </div>
      );
    }

    if (!detailsExist && !hasOutline) {
      return renderRawPreview();
    }

    // Detailed scene cards
    const scenes = parsedDetailedScenes;
    const collapsedCount = Math.max(0, scenes.length - open.size);

    return (
      <div className="sp-cards">

        {(isExpanded ? scenes : scenes.slice(0, 5)).map((s, idx) => {
          const id = sceneId(s, idx);
          const isOpen = open.has(id);
          return (
            <div
              key={id}
              className={`sp-card ${isOpen ? 'open' : ''}`}
              onClick={() => toggleOpen(id)}
              role="button"
              tabIndex={0}
            >
              <div className="sp-header">
                <div className="sp-title">
                  <span className="sp-heading">
                    <strong>{s.number}. {s.title || `Scene ${s.number}`}</strong>
                    {s.duration ? <span className="sp-duration"> [{s.duration}]</span> : null}
                  </span>
                </div>
              </div>

              {isEditing && (
                <button
                  className="sp-card-edit-btn"
                  title="Edit this scene"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation(); // don’t toggle the card open/closed
                    setEditingSceneId(id); // we’ll use this in step 2
                    const orig = (Array.isArray(detailedScreenplay?.scenes) ? detailedScreenplay.scenes : [])[idx] || {};
                    setDraftsById(d => d[id] ? d : ({ ...d, [id]: makeDraftFromScene(orig) }));
                  }}
                >
                  ✎
                </button>
              )}

              {isEditing && editingSceneId === id ? (
                (() => {
                  // --- editor mode ---
                  const originalScene = (Array.isArray(detailedScreenplay?.scenes) ? detailedScreenplay.scenes : [])[idx] || {};
                  const draft = draftsById[id] ?? makeDraftFromScene(originalScene);

                  const set = (patch) =>
                    setDraftsById((d) => ({ ...d, [id]: { ...(d[id] || draft), ...patch } }));

                  const shotMatrixValid = (() => {
                    try { return Array.isArray(JSON.parse((draft.shot_matrix_json ?? '[]'))); }
                    catch { return false; }
                  })();

                  const save = async () => {
                    const sceneDraft = draftsById[id] || draft;

                    // 1. Validate
                    if (onValidateContent) {
                      setValidatingSceneId(id);
                      setValidationErrors(prev => ({ ...prev, [id]: null })); // Clear prev error

                      // Reconstruct text for context
                      const textToValidate = [
                        `Title: ${sceneDraft.title}`,
                        `Duration: ${sceneDraft.duration_minutes}`,
                        `Synopsis: ${sceneDraft.synopsis}`,
                        `Visual Prompt: ${sceneDraft.visual}`,
                        `Scene Image Prompt: ${sceneDraft.scene_image_prompt}`,
                        `Environment: ${sceneDraft.environment}`,
                        `Coverage: ${sceneDraft.coverage}`,
                        `Transitions: ${sceneDraft.transitions}`,
                        `Editorial: ${sceneDraft.editorial_intent}`,
                        `Lighting: ${sceneDraft.lighting_plan}`,
                        `Music: ${sceneDraft.background_music}`,
                        `Character Expressions: ${(sceneDraft.character_expressions || []).map(x => `${x.key}: ${x.value}`).join(', ')}`,
                        `Character Attire: ${(sceneDraft.character_attire || []).map(x => `${x.key}: ${x.value}`).join(', ')}`,
                        `Scene Bible - Lens: ${sceneDraft.scene_bible?.lens_mm}`,
                        `Scene Bible - Aspect: ${sceneDraft.scene_bible?.aspect}`,
                        `Scene Bible - Palette: ${sceneDraft.scene_bible?.palette}`,
                        `Scene Bible - Grade: ${sceneDraft.scene_bible?.grade}`,
                        `Scene Bible - Time: ${sceneDraft.scene_bible?.time_of_day}`,
                        `Scene Bible - Continuity: ${sceneDraft.scene_bible?.continuity_notes}`,
                        `Dialogues: ${(sceneDraft.dialogues || []).join('\n')}`,
                        `Shot Matrix: ${sceneDraft.shot_matrix_json}`
                      ].join('\n\n');

                      const check = await onValidateContent(textToValidate);
                      setValidatingSceneId(null);

                      if (check && !check.valid) {
                        setValidationErrors(prev => ({ ...prev, [id]: check.reason }));
                        return;
                      }
                    }

                    const scenes = Array.isArray(detailedScreenplay?.scenes)
                      ? [...detailedScreenplay.scenes]
                      : [];
                    scenes[idx] = applyDraftToScene(originalScene, sceneDraft);
                    const nextScreenplay = { ...(detailedScreenplay || {}), scenes };
                    onSaveEditedScreenplay?.(nextScreenplay);
                    setEditingSceneId(null);
                  };

                  const cancel = () => {
                    setDraftsById((d) => {
                      const n = { ...d };
                      delete n[id];
                      return n;
                    });
                    setEditingSceneId(null);
                  };

                  return (
                    <div
                      className="sp-card-editor"
                      style={{ display: "grid", gap: 12 }}
                      onClick={(e) => e.stopPropagation()}
                    >
                      {/* Basics */}
                      <div style={{ display: "grid", gridTemplateColumns: "1fr 180px", gap: 12 }}>
                        <div>
                          <div className="field-label">Title</div>
                          <input className="sp-input"
                            value={draft.title}
                            onChange={(e) => set({ title: e.target.value })}
                          />
                        </div>
                        <div>
                          <div className="field-label">Duration (min)</div>
                          <input className="sp-input"
                            type="number"
                            value={draft.duration_minutes}
                            onChange={(e) => set({ duration_minutes: e.target.value })}
                          />
                        </div>
                      </div>

                      <div>
                        <div className="field-label">Synopsis</div>
                        <textarea className="sp-textarea"
                          rows={4}
                          value={draft.synopsis}
                          onChange={(e) => set({ synopsis: e.target.value })}
                        />
                      </div>

                      <div>
                        <div className="field-label">Visual Prompt</div>
                        <textarea className="sp-textarea"
                          rows={4}
                          value={draft.visual}
                          onChange={(e) => set({ visual: e.target.value })}
                        />
                      </div>

                      <div>
                        <div className="field-label">Scene Image Prompt</div>
                        <textarea
                          className="sp-textarea"
                          rows={4}
                          value={draft.scene_image_prompt}
                          onChange={(e) => set({ scene_image_prompt: e.target.value })}
                        />
                      </div>

                      <div>
                        <div className="field-label">Environment</div>
                        <textarea className="sp-textarea"
                          rows={3}
                          value={draft.environment}
                          onChange={(e) => set({ environment: e.target.value })}
                        />
                      </div>

                      {/* Coverage / Lighting / Music */}
                      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }}>
                        <div>
                          <div className="field-label">Coverage</div>
                          <textarea className="sp-textarea"
                            rows={3}
                            value={draft.coverage}
                            onChange={(e) => set({ coverage: e.target.value })}
                          />
                        </div>
                        <div>
                          <div className="field-label">Transitions</div>
                          <textarea className="sp-textarea"
                            rows={3}
                            value={draft.transitions}
                            onChange={(e) => set({ transitions: e.target.value })}
                          />
                        </div>
                        <div>
                          <div className="field-label">Editorial Intent</div>
                          <textarea className="sp-textarea"
                            rows={3}
                            value={draft.editorial_intent}
                            onChange={(e) => set({ editorial_intent: e.target.value })}
                          />
                        </div>
                      </div>

                      <div>
                        <div className="field-label">Lighting Plan</div>
                        <textarea className="sp-textarea"
                          rows={3}
                          value={draft.lighting_plan}
                          onChange={(e) => set({ lighting_plan: e.target.value })}
                        />
                      </div>

                      <div>
                        <div className="field-label">Background Music</div>
                        <input className="sp-input"
                          value={draft.background_music}
                          onChange={(e) => set({ background_music: e.target.value })}
                        />
                      </div>

                      {/* Key/Value maps */}
                      <KVListEditor
                        label="Character Expressions"
                        items={draft.character_expressions}
                        onChange={(v) => set({ character_expressions: v })}
                      />
                      <KVListEditor
                        label="Character Attire"
                        items={draft.character_attire}
                        onChange={(v) => set({ character_attire: v })}
                      />



                      {/* Scene Bible */}
                      <div
                        className="scene-bible-editor"
                        style={{
                          display: "grid",
                          gridTemplateColumns: "repeat(3, 1fr)",
                          gap: 12,
                        }}
                      >
                        <div>
                          <div className="field-label">Lens (mm)</div>
                          <input className="sp-input"
                            value={draft.scene_bible.lens_mm}
                            onChange={(e) =>
                              set({
                                scene_bible: { ...draft.scene_bible, lens_mm: e.target.value },
                              })
                            }
                          />
                        </div>
                        <div>
                          <div className="field-label">Aspect</div>
                          <input className="sp-input"
                            value={draft.scene_bible.aspect}
                            onChange={(e) =>
                              set({
                                scene_bible: { ...draft.scene_bible, aspect: e.target.value },
                              })
                            }
                          />
                        </div>
                        <div>
                          <div className="field-label">Palette</div>
                          <input className="sp-input"
                            value={draft.scene_bible.palette}
                            onChange={(e) =>
                              set({
                                scene_bible: { ...draft.scene_bible, palette: e.target.value },
                              })
                            }
                          />
                        </div>
                        <div>
                          <div className="field-label">Grade</div>
                          <input className="sp-input"
                            value={draft.scene_bible.grade}
                            onChange={(e) =>
                              set({
                                scene_bible: { ...draft.scene_bible, grade: e.target.value },
                              })
                            }
                          />
                        </div>
                        <div>
                          <div className="field-label">Time of day</div>
                          <input className="sp-input"
                            value={draft.scene_bible.time_of_day}
                            onChange={(e) =>
                              set({
                                scene_bible: {
                                  ...draft.scene_bible,
                                  time_of_day: e.target.value,
                                },
                              })
                            }
                          />
                        </div>
                        <div style={{ gridColumn: "1 / -1" }}>
                          <div className="field-label">Continuity notes (one per line)</div>
                          <textarea className="sp-textarea"
                            rows={3}
                            value={draft.scene_bible.continuity_notes}
                            onChange={(e) =>
                              set({
                                scene_bible: {
                                  ...draft.scene_bible,
                                  continuity_notes: e.target.value,
                                },
                              })
                            }
                          />
                        </div>
                      </div>

                      <div>
                        <div className="field-label">Shot Matrix (JSON)</div>
                        <textarea
                          className="sp-textarea code"
                          rows={8}
                          value={draft.shot_matrix_json}
                          onChange={(e) => set({ shot_matrix_json: e.target.value })}
                        />
                        {!shotMatrixValid ? (
                          <div className="sp-error">Shot Matrix must be a valid JSON array.</div>
                        ) : (
                          <div className="sp-help">Tip: provide an array of shot objects. Example: [{"{"}label{"}"} , {"{"}camera{"}"} , {"{"}action{"}"} , ...]</div>
                        )}
                      </div>

                      <div className="sp-editor-actions" style={{ alignItems: 'center', gap: 10 }}>
                        <button className="sp-btn primary" onClick={save} disabled={!shotMatrixValid || validatingSceneId === id}>
                          {validatingSceneId === id ? "Validating..." : "Save"}
                        </button>
                        <button className="sp-btn" onClick={cancel}>Cancel</button>
                        {validationErrors[id] && (
                          <span className="sp-help" style={{ color: '#ff6b6b', margin: 0 }}>
                            {validationErrors[id]}
                          </span>
                        )}
                      </div>
                    </div>
                  );
                })()
              ) : (
                // --- read-only mode ---
                <>
                  {s.logline && <div className="sp-subtitle">{s.logline}</div>}
                  {s.synopsis && <div className="sp-synopsis">{s.synopsis}</div>}
                  {isOpen && (
                    <div className="sp-body" onClick={(e) => e.stopPropagation()}>
                      {s.visualPrompt && (
                        <Section title="Visual Prompt">
                          {s.visualPrompt}
                        </Section>
                      )}

                      {s.sceneImagePrompt && (
                        <Section title="Scene Image Prompt">
                          {s.sceneImagePrompt}
                        </Section>
                      )}

                      {Array.isArray(s.characters) && s.characters.length && (
                        <Section title="Characters">
                          {s.characters.join(' · ')}
                        </Section>
                      )}

                      {s.environment && (
                        <Section title="Environment">
                          {s.environment}
                        </Section>
                      )}

                      {s.sceneBible && (
                        <div className="nw-info-block">
                          <div className="nw-label">Scene Bible</div>
                          <ul className="nw-list">
                            {s.sceneBible.lens_mm ? <li><span className="nw-list-label">Lens:</span> {s.sceneBible.lens_mm}mm</li> : null}
                            {s.sceneBible.aspect ? <li><span className="nw-list-label">Aspect:</span> {s.sceneBible.aspect}</li> : null}
                            {s.sceneBible.time_of_day ? <li><span className="nw-list-label">Time:</span> {s.sceneBible.time_of_day}</li> : null}
                            {s.sceneBible.palette ? <li><span className="nw-list-label">Palette:</span> {s.sceneBible.palette}</li> : null}
                            {s.sceneBible.grade ? <li><span className="nw-list-label">Grade:</span> {s.sceneBible.grade}</li> : null}
                            {Array.isArray(s.sceneBible.continuity_notes) && s.sceneBible.continuity_notes.length ? (
                              <li><span className="nw-list-label">Continuity:</span> {s.sceneBible.continuity_notes.join('; ')}</li>
                            ) : null}
                          </ul>
                        </div>
                      )}

                      {s.coveragePlan && (
                        <CoverageBlock plan={s.coveragePlan} />
                      )}
                      {s.lightingPlan && (
                        <Section title="Lighting Plan">
                          <div className="pre-wrap">{s.lightingPlan}</div>
                        </Section>
                      )}

                      {Array.isArray(s.characterExperessions) && s.characterExperessions.length > 0 && (
                        <div className="nw-info-block">
                          <div className="nw-label">Character Expressions</div>
                          <ul className="nw-list">
                            {s.characterExperessions.map((c, i) => <li key={i}>{c}</li>)}
                          </ul>
                        </div>
                      )}

                      {Array.isArray(s.characterAttire) && s.characterAttire.length > 0 && (
                        <div className="nw-info-block">
                          <div className="nw-label">Character Attire</div>
                          <ul className="nw-list">
                            {s.characterAttire.map((c, i) => <li key={i}>{c}</li>)}
                          </ul>
                        </div>
                      )}

                      {s.backgroundMusic && (
                        <Section title="Background Music">
                          {s.backgroundMusic}
                        </Section>
                      )}



                      {Array.isArray(s.shotMatrix) && s.shotMatrix.length > 0 && (
                        <div className="nw-info-block">
                          <div className="nw-label">Shot Matrix</div>
                          <ShotMatrixTable rows={s.shotMatrix} />
                        </div>
                      )}

                      {s.fx && (
                        <Section title="FX / Notes">
                          {s.fx}
                        </Section>
                      )}
                    </div>
                  )}
                </>
              )}

            </div>
          );
        })}
      </div>
    );
  })();

  return (
    <div className="screenplay-tile">
      {/* Header (kept minimal) */}
      {/* <div className="header-container">
        <h2>Detailed Screenplay</h2>
        <button title={maximizeScreenPlay ? 'hide' : 'show'} className="min-max-button" onClick={handleShowHideScreenplay}>
          {maximizeScreenPlay ? '📰' : '🗞'}
        </button>
      </div> */}
      {maximizeScreenPlay ? body : null}
    </div>
  );
}
export default React.memo(ScreenplayTile);
